// ASSIGNMENTS_SINGLE_SCROLL_WORKSPACE_V1
import { getEffectiveToday } from '../../utils/systemTime';
import { printMeetingPdf } from '../../utils/meetingPdf';
import { loadMeetingAssignmentOverview } from '../../utils/meetingAssignmentOverview';
import { useState, useEffect } from 'react';
import {
  useLocation,
  useNavigate
} from 'react-router-dom';
import AssignmentGrading from './assignments/AssignmentGrading';
import StudentProgress from './assignments/StudentProgress';
import AllGrading from './assignments/AllGrading'; // ALL_GRADING_V1

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_KEY = process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY;

const TABS = [
  { key: 'grading', label: 'Grade Assignments' },
  { key: 'progress', label: 'Student Progress' },
  { key: 'all-grading', label: 'All Grading' },
];

// COURSE_SEMESTER_SORT_V1
function sortCoursesBySemesterAndName(
  courses
) {
  return [
    ...(
      Array.isArray(courses)
        ? courses
        : []
    )
  ].sort(
    (a, b) => {
      const aStart =
        String(
          a?.semesters
            ?.start_date ||
          ''
        );

      const bStart =
        String(
          b?.semesters
            ?.start_date ||
          ''
        );

      /*
       * ISO dates sort chronologically as strings.
       * Newest semester first.
       */
      const byStartDate =
        bStart.localeCompare(
          aStart
        );

      if (byStartDate !== 0) {
        return byStartDate;
      }


      /*
       * Keeps courses from the same semester together even
       * if two semester records happen to share a start date.
       */
      const bySemester =
        String(
          b?.semesters?.name ||
          ''
        ).localeCompare(
          String(
            a?.semesters?.name ||
            ''
          ),
          undefined,
          {
            sensitivity: 'base'
          }
        );

      if (bySemester !== 0) {
        return bySemester;
      }


      const aName =
        String(
          a?.course_titles
            ?.name ||
          a?.name ||
          ''
        );

      const bName =
        String(
          b?.course_titles
            ?.name ||
          b?.name ||
          ''
        );

      return aName.localeCompare(
        bName,
        undefined,
        {
          sensitivity: 'base'
        }
      );
    }
  );
}


async function fetchAccessibleCourses(session) {
  const selectFields =
    'id,name,class_day,class_days,class_time,class_times,pd_due_day,' +
    'cq_clarificatory_enabled,cq_clarificatory_allowed,qi_enabled,' +
    'qi_groups_per_stream,streams_enabled,group_based_attendance,' +
    'grading_mode,pd_enabled,cq_enabled,attendance_enabled,self_eval_enabled,' +
    'course_progress_enabled,' +
    'mid_eval_open_date,mid_eval_close_date,final_eval_open_date,' +
    'final_eval_close_date,course_title_id,semester_id,instructor_id,' +
    'course_titles(name),semesters(name,start_date)';

  const headers = {
    'apikey': SUPABASE_KEY,
    'Authorization': 'Bearer ' + SUPABASE_KEY
  };

  if (session.isSuperadmin) {
    const response = await fetch(
      `${SUPABASE_URL}/rest/v1/courses?select=${selectFields}&order=created_at.desc`,
      { headers }
    );
    const rows =
      await response.json();

    return sortCoursesBySemesterAndName(
      rows
    );
  }

  const [ownedResponse, accessResponse] = await Promise.all([
    fetch(
      `${SUPABASE_URL}/rest/v1/courses?instructor_id=eq.${session.instructorId}&select=${selectFields}&order=created_at.desc`,
      { headers }
    ),
    fetch(
      `${SUPABASE_URL}/rest/v1/course_access?instructor_id=eq.${session.instructorId}&select=courses(${selectFields})`,
      { headers }
    )
  ]);

  const owned = await ownedResponse.json();
  const accessRows = await accessResponse.json();
  const secondaryCourses = (Array.isArray(accessRows) ? accessRows : [])
    .map(row => row.courses)
    .filter(Boolean);

  const byId = new Map();
  [...(Array.isArray(owned) ? owned : []), ...secondaryCourses]
    .forEach(course => byId.set(course.id, course));

  return sortCoursesBySemesterAndName(
    Array.from(
      byId.values()
    )
  );
}

async function getEnrolmentIdList(courseId) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/enrolments?course_id=eq.${courseId}&active=eq.true&select=id`,
    { headers: { 'apikey': SUPABASE_KEY, 'Authorization': 'Bearer ' + SUPABASE_KEY } }
  );
  const data = await response.json();
  return data.map(e => e.id).join(',');
}

async function fetchAttendanceGroupContext(courseId) {
  const headers = {
    'apikey': SUPABASE_KEY,
    'Authorization': 'Bearer ' + SUPABASE_KEY
  };

  const [membershipsResponse, weeksResponse] = await Promise.all([
    fetch(
      `${SUPABASE_URL}/rest/v1/attendance_group_memberships?select=enrolment_id,attendance_group_id,attendance_groups!inner(course_id)&attendance_groups.course_id=eq.${courseId}`,
      { headers }
    ),
    fetch(
      `${SUPABASE_URL}/rest/v1/attendance_group_weeks?select=attendance_group_id,course_week_id,attendance_groups!inner(course_id)&attendance_groups.course_id=eq.${courseId}`,
      { headers }
    )
  ]);

  const memberships = await membershipsResponse.json();
  const groupWeeks = await weeksResponse.json();

  return {
    memberships: Array.isArray(memberships) ? memberships : [],
    groupWeeks: Array.isArray(groupWeeks) ? groupWeeks : []
  };
}

async function fetchCourseInstructors(courseId) {
  const headers = {
    'apikey': SUPABASE_KEY,
    'Authorization': 'Bearer ' + SUPABASE_KEY
  };

  const [courseResponse, accessResponse] = await Promise.all([
    fetch(
      `${SUPABASE_URL}/rest/v1/courses?id=eq.${courseId}&select=instructor_id&limit=1`,
      { headers }
    ),
    fetch(
      `${SUPABASE_URL}/rest/v1/course_access?course_id=eq.${courseId}&select=instructor_id,instructors(id,name,email)`,
      { headers }
    )
  ]);

  const courseRows = await courseResponse.json();
  const accessRows = await accessResponse.json();
  const primaryId = courseRows?.[0]?.instructor_id || null;

  let primaryInstructor = null;
  if (primaryId) {
    const primaryResponse = await fetch(
      `${SUPABASE_URL}/rest/v1/instructors?id=eq.${primaryId}&select=id,name,email&limit=1`,
      { headers }
    );
    const primaryRows = await primaryResponse.json();
    primaryInstructor = primaryRows?.[0] || null;
  }

  const secondaries = (Array.isArray(accessRows) ? accessRows : [])
    .map(row => row.instructors)
    .filter(Boolean)
    .filter(instructor => instructor.id !== primaryId)
    .sort((x, y) => (x.name || '').localeCompare(y.name || ''));

  return [primaryInstructor, ...secondaries].filter(Boolean);
}

function getInstructorInitials(name) {
  const text = String(name || '').trim();
  const capitals = text.match(/[A-Z]/g);

  if (capitals?.length) {
    return capitals.join('').slice(0, 3);
  }

  return text
    .split(/\s+/)
    .filter(Boolean)
    .map(part => part.charAt(0).toUpperCase())
    .join('')
    .slice(0, 3) || 'I';
}

function latestFeedback(feedback) {
  if (!feedback) return null;
  const rows = Array.isArray(feedback) ? feedback : [feedback];

  return [...rows].sort((a, b) => {
    const aTime = new Date(
      a.submitted_at || a.updated_at || a.created_at || 0
    ).getTime();
    const bTime = new Date(
      b.submitted_at || b.updated_at || b.created_at || 0
    ).getTime();
    return bTime - aTime;
  })[0] || null;
}

function getCourseName(course) {
  return (course.course_titles?.name || course.name || 'Untitled') +
    ' — ' + (course.semesters?.name || '');
}

export default function Assignments({
  session,
  initialCourseId = null,
  onCourseSelected
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [selectedCourseId, setSelectedCourseId] = useState('');
  const [activeTab, setActiveTab] = useState(() => {
    const requested =
      new URLSearchParams(
        window.location.search
      ).get('tab');

    return TABS.some(
      tab =>
        tab.key === requested
    )
      ? requested
      : 'grading';
  });
  const [loading, setLoading] = useState(true);
  const [pdfLoading, setPdfLoading] = useState({ mid: false, final: false });
  const [pdfPackets, setPdfPackets] = useState({ mid: [], final: [] });
  const [pdfNotice, setPdfNotice] = useState({ mid: '', final: '' });

  useEffect(() => {
    fetchAccessibleCourses(session)
      .then(c => {
        setCourses(c);

        if (initialCourseId && c.some(course => course.id === initialCourseId)) {
          setSelectedCourseId(initialCourseId);
        } else if (c.length === 1) {
          setSelectedCourseId(c[0].id);
        } else {
          setSelectedCourseId('');
        }
      })
      .finally(() => setLoading(false));
  }, [session, initialCourseId]);

  const selectedCourse = courses.find(c => c.id === selectedCourseId) || null;

  useEffect(() => {
  if (
    selectedCourseId &&
    onCourseSelected
  ) {
    onCourseSelected(
      selectedCourseId
    );
  }
}, [
  selectedCourseId,
  onCourseSelected
]);

  const canManageSelectedCourse = !!selectedCourse && (
    session.isSuperadmin ||
    selectedCourse.instructor_id === session.instructorId
  );

  useEffect(() => {
    if (
      activeTab === 'progress' &&
      !canManageSelectedCourse
    ) {
      setActiveTab('grading');
    }
  }, [
    activeTab,
    canManageSelectedCourse
  ]);

  async function handlePrintMeetingPacket(
    meetingType,
    packet
  ) {
    if (!selectedCourse || !packet) {
      return;
    }

    const key =
      meetingType === 'Mid-Term'
        ? 'mid'
        : 'final';

    const courseName =
      selectedCourse.course_titles?.name ||
      selectedCourse.name ||
      'Course';

    const semester =
      selectedCourse.semesters?.name ||
      '';

    const suffix =
      packet.suffix
        ? ` - ${packet.suffix}`
        : '';

    try {
      await printMeetingPdf({
        courseName,
        semester,
        meetingType,
        students:
          packet.students || [],
        packetLabel:
          packet.instructorName || '',
        fileName:
          `${courseName} - ${meetingType}${suffix}.pdf`
      });
    } catch (err) {
      console.error(
        'Meeting PDF print failed:',
        err
      );

      setPdfNotice(prev => ({
        ...prev,
        [key]:
          err?.message ||
          'The meeting PDF could not be printed.'
      }));
    }
  }

  // MEETING_PDF_SELF_EVAL_VISIBILITY_20260920
  async function handleGeneratePdf(meetingType) {
    if (
      selectedCourse?.self_eval_enabled !== true
    ) {
      return;
    }

    const key = meetingType === 'Mid-Term' ? 'mid' : 'final';
    setPdfLoading(prev => ({ ...prev, [key]: true }));
    setPdfPackets(prev => ({ ...prev, [key]: [] }));
    setPdfNotice(prev => ({ ...prev, [key]: '' }));

    try {
      const courseId = selectedCourse.id;
      
      const ids = await getEnrolmentIdList(courseId);

      const [
        enrolments,
        weeks,
        meetings,
        attendance,
        cqSubs,
        pdSubs,
        selfEvalResponses,
        instructors
      ] = await Promise.all([
        fetch(
          `${SUPABASE_URL}/rest/v1/enrolments?course_id=eq.${courseId}&active=eq.true&select=*,students(id,student_code,english_name,chinese_name,photo_drive_file_id)`,
          { headers: { 'apikey': SUPABASE_KEY, 'Authorization': 'Bearer ' + SUPABASE_KEY } }
        ).then(r => r.json()),
        fetch(
          `${SUPABASE_URL}/rest/v1/course_weeks?course_id=eq.${courseId}&select=*&order=week_number.asc`,
          { headers: { 'apikey': SUPABASE_KEY, 'Authorization': 'Bearer ' + SUPABASE_KEY } }
        ).then(r => r.json()),
        fetch(
          `${SUPABASE_URL}/rest/v1/student_meetings?course_id=eq.${courseId}&meeting_type=eq.${encodeURIComponent(meetingType)}&select=*,student_meeting_slots(*)&order=meeting_date.asc`,
          { headers: { 'apikey': SUPABASE_KEY, 'Authorization': 'Bearer ' + SUPABASE_KEY } }
        ).then(r => r.json()),
        ids
          ? fetch(
              `${SUPABASE_URL}/rest/v1/attendance?enrolment_id=in.(${ids})&select=*`,
              { headers: { 'apikey': SUPABASE_KEY, 'Authorization': 'Bearer ' + SUPABASE_KEY } }
            ).then(r => r.json())
          : Promise.resolve([]),
        ids
          ? fetch(
              `${SUPABASE_URL}/rest/v1/cq_submissions?enrolment_id=in.(${ids})&select=*`,
              { headers: { 'apikey': SUPABASE_KEY, 'Authorization': 'Bearer ' + SUPABASE_KEY } }
            ).then(r => r.json())
          : Promise.resolve([]),
        ids
          ? fetch(
              `${SUPABASE_URL}/rest/v1/pd_submissions?enrolment_id=in.(${ids})&select=*,pd_feedback(*)`,
              { headers: { 'apikey': SUPABASE_KEY, 'Authorization': 'Bearer ' + SUPABASE_KEY } }
            ).then(r => r.json())
          : Promise.resolve([]),
        ids
          ? fetch(
              `${SUPABASE_URL}/rest/v1/self_evaluation_responses?enrolment_id=in.(${ids})&select=*`,
              { headers: { 'apikey': SUPABASE_KEY, 'Authorization': 'Bearer ' + SUPABASE_KEY } }
            ).then(r => r.json())
          : Promise.resolve([]),
        fetchCourseInstructors(courseId)
      ]);

      const attendanceGroupContext =
        selectedCourse.group_based_attendance
          ? await fetchAttendanceGroupContext(courseId)
          : { memberships: [], groupWeeks: [] };

      const otherAssignmentOverview =
        await loadMeetingAssignmentOverview(courseId, enrolments);

      const today = getEffectiveToday();
      const weeksToDate = weeks.filter(w => w.class_date <= today);

      const allSlots = meetings
        .flatMap(m =>
          (m.student_meeting_slots || []).map(s => ({
            ...s,
            meetingDate: m.meeting_date
          }))
        )
        .sort((a, b) => a.meeting_time.localeCompare(b.meeting_time));

      const studentData = allSlots
        .map(slot => {
          const enrolment = enrolments.find(
            e => e.student_id === slot.student_id
          );
          if (!enrolment) return null;

          const student = enrolment.students;
          const attendanceMembership =
            attendanceGroupContext.memberships.find(
              row => row.enrolment_id === enrolment.id
            );

          const attendanceWeekIds = new Set(
            attendanceMembership
              ? attendanceGroupContext.groupWeeks
                  .filter(
                    row =>
                      row.attendance_group_id ===
                      attendanceMembership.attendance_group_id
                  )
                  .map(row => row.course_week_id)
              : []
          );

          const attWeeks = selectedCourse.group_based_attendance
            ? weeksToDate.filter(w => attendanceWeekIds.has(w.id))
            : weeksToDate.filter(w => w.attendance_enabled);

          const cqWeeks = weeksToDate.filter(w => w.cq_enabled);
          const pdWeeks = weeksToDate.filter(w => w.pd_enabled);

          const attPresent = attendance.filter(a =>
            a.enrolment_id === enrolment.id &&
            a.present &&
            attWeeks.some(w => w.id === a.week_id)
          ).length;

          const cqCount = cqSubs.filter(c =>
            c.enrolment_id === enrolment.id &&
            cqWeeks.some(w => w.id === c.week_id)
          ).length;

          const pdSubmitted = pdSubs.filter(p =>
            p.enrolment_id === enrolment.id &&
            p.submitted &&
            pdWeeks.some(w => w.id === p.week_id)
          );

          const pdNotes = pdSubmitted
            .map(p => ({
              submission: p,
              feedback: latestFeedback(p.pd_feedback)
            }))
            .filter(item => item.feedback?.instructor_note)
            .map(item => ({
              weekNumber: weeks.find(
                w => w.id === item.submission.week_id
              )?.week_number,
              note: item.feedback.instructor_note
            }));

          const otherAssignments =
            otherAssignmentOverview.get(String(enrolment.id)) || [];

          const section =
            meetingType === 'Mid-Term'
              ? 'Mid-Semester'
              : 'Final';

          const selfEval = selfEvalResponses.find(r =>
            r.enrolment_id === enrolment.id &&
            r.section === section
          );

          const answers = selfEval?.answers || {};
          const snapshot = selfEval?.form_snapshot;
          const instructorComments =
            selfEval?.instructor_comment || {};

          const selfEvalItems = snapshot?.questions
            ? snapshot.questions
                .filter(q => q.type !== 'GRADE')
                .map(q => ({
                  sectionTitle: q.section_title,
                  answer: answers[q.id]?.value || '',
                  justification: q.show_justification
                    ? (answers[q.id]?.justification || '')
                    : '',
                  instructorComment:
                    instructorComments[q.id] || ''
                }))
            : [];

          if (snapshot?.courseSpecific) {
            selfEvalItems.push({
              sectionTitle:
                snapshot.courseSpecific.section_title ||
                'Course-Specific',
              answer: answers.courseSpecific?.value || '',
              justification: '',
              instructorComment:
                instructorComments.courseSpecific || ''
            });
          }

          const gradeQuestion = snapshot?.questions?.find(
            q => q.type === 'GRADE'
          );

          const suggestedGrade = gradeQuestion
            ? answers[gradeQuestion.id]?.value || '—'
            : '—';

          return {
            studentCode: student.student_code,
            photoFileId: student.photo_drive_file_id || null,
            englishName: student.english_name || '',
            chineseName: student.chinese_name || '',
            meetingTime: slot.meeting_time?.slice(0, 5) || '—',
            meetingDate: slot.meetingDate,
            assignedInstructorId:
              enrolment.assigned_instructor_id || null,
            attPresent,
            attTotal: attWeeks.length,
            cqCount,
            cqTotal: cqWeeks.length,
            pdCount: pdSubmitted.length,
            pdTotal: pdWeeks.length,
            pdNotes,
            otherAssignments,
            suggestedGrade,
            finalGrade: selfEval?.final_grade || '',
            selfEvalItems
          };
        })
        .filter(Boolean);

      const divisionActive =
        instructors.length > 1 &&
        studentData.some(
          student =>
            student.assignedInstructorId
        );

      let packets = [];

      if (divisionActive) {
        for (
          const instructor
          of instructors
        ) {
          const instructorStudents =
            studentData.filter(
              student =>
                student.assignedInstructorId ===
                instructor.id
            );

          if (
            instructorStudents.length === 0
          ) {
            continue;
          }

          const initials =
            getInstructorInitials(
              instructor.name
            );

          packets.push({
            label: `${initials} PDF`,
            suffix: initials,
            instructorId:
              instructor.id,
            instructorName:
              instructor.name || initials,
            students:
              instructorStudents
          });
        }

        const unassignedCount =
          studentData.filter(
            student =>
              !student.assignedInstructorId
          ).length;

        const notices = [];

        if (unassignedCount > 0) {
          notices.push(
            `${unassignedCount} student${unassignedCount === 1 ? '' : 's'} ` +
            `with meeting slots ${unassignedCount === 1 ? 'is' : 'are'} ` +
            'not assigned to an instructor and is not included in an instructor PDF.'
          );
        }

        if (packets.length > 1) {
          notices.push(
            'Choose the instructor PDF to print.'
          );
        }

        setPdfNotice(prev => ({
          ...prev,
          [key]:
            notices.join(' ')
        }));
      } else {
        packets = [
          {
            label: 'PDF',
            suffix: '',
            instructorId: null,
            instructorName: '',
            students: studentData
          }
        ];
      }

      if (packets.length === 0) {
        throw new Error(
          'No students with meeting slots were available to print.'
        );
      }

      setPdfPackets(prev => ({
        ...prev,
        [key]: packets
      }));

      // Match the syllabus workflow when there is a single packet:
      // open the native browser print dialog immediately.
      if (packets.length === 1) {
        await handlePrintMeetingPacket(
          meetingType,
          packets[0]
        );
      }
    } catch (err) {
      console.error('PDF generation error:', err);
      setPdfNotice(prev => ({
        ...prev,
        [key]: err?.message || 'PDF generation failed.'
      }));
    } finally {
      setPdfLoading(prev => ({ ...prev, [key]: false }));
    }
  }

  // ASSIGNMENTS_REFRESH_TAB_SYNC
  useEffect(() => {
    const params =
      new URLSearchParams(
        location.search
      );

    const requested =
      params.get('tab');

    if (!requested) {
      return;
    }

    const valid =
      TABS.some(
        tab =>
          tab.key === requested
      ) &&
      (
        requested !== 'progress' ||
        (
          canManageSelectedCourse &&
          selectedCourse
            ?.course_progress_enabled !==
            false
        )
      );

    if (
      valid &&
      requested !== activeTab
    ) {
      setActiveTab(requested);
      return;
    }

    if (!valid) {
      if (activeTab !== 'grading') {
        setActiveTab('grading');
      }

      params.set(
        'tab',
        'grading'
      );

      navigate(
        `${location.pathname}?${params.toString()}`,
        { replace: true }
      );
    }
  }, [
    activeTab,
    canManageSelectedCourse,
    selectedCourse
      ?.course_progress_enabled,
    location.pathname,
    location.search,
    navigate
  ]);

  function selectRefreshSafeAssignmentTab(
    nextTab
  ) {
    setActiveTab(nextTab);

    const params =
      new URLSearchParams(
        location.search
      );

    params.set(
      'tab',
      nextTab
    );

    navigate(
      `${location.pathname}?${params.toString()}`,
      { replace: true }
    );
  }

  function handleOpenAllGradingItem(item) {
    const courseId =
      String(
        item?.courseId ||
        ''
      ).trim();

    if (!courseId) {
      return;
    }

    let gradingTab = '';

    if (
      item?.kind ===
        'assignment' &&
      item?.assignmentTypeId
    ) {
      gradingTab =
        'assignment-type:' +
        String(
          item.assignmentTypeId
        );

    } else if (
      item?.kind === 'pd'
    ) {
      gradingTab =
        'Philosophical Diary';

    } else if (
      item?.kind ===
        'selfeval'
    ) {
      gradingTab =
        'Self-Evaluation';
    }

    if (!gradingTab) {
      return;
    }

    try {
      const storageBase =
        'student-system:grading:' +
        courseId;

      window.sessionStorage.setItem(
        storageBase + ':tab',
        gradingTab
      );

      window.sessionStorage.removeItem(
        storageBase + ':target'
      );
    } catch {
      // Navigation persistence is optional.
    }

    setSelectedCourseId(
      courseId
    );

    // ALL_GRADING_NAV_RACE_FIX_V1
    /*
     * Let the URL drive the tab change.
     *
     * Setting activeTab here while location.search still says
     * "all-grading" allows the URL-sync effect to immediately
     * switch us back to All Grading.
     */
    const params =
      new URLSearchParams(
        location.search
      );

    params.set(
      'courseId',
      courseId
    );

    params.set(
      'tab',
      'grading'
    );

    navigate(
      location.pathname +
        '?' +
        params.toString(),
      {
        replace: true
      }
    );
  }


  if (loading) {
    return (
      <div style={styles.muted}>
        Loading...
      </div>
    );
  }

  const visibleTabs =
    TABS.filter(tab => {
      if (
        tab.key === 'grading' ||
        tab.key === 'all-grading'
      ) {
        return true;
      }

      if (
        tab.key === 'progress'
      ) {
        return (
          canManageSelectedCourse &&
          selectedCourse
            ?.course_progress_enabled !==
            false
        );
      }

      return false;
    });


  return (
    <div>
      <div style={styles.header}>
        <h1 style={styles.heading}>
          Assignments
        </h1>

        <select
          style={
            styles.courseSelect
          }
          value={
            selectedCourseId
          }
          onChange={event => {
            setSelectedCourseId(
              event.target.value
            );

            if (
              event.target.value
            ) {
              selectRefreshSafeAssignmentTab(
                'grading'
              );
            }
          }}
        >
          <option value="">
            Select a course
          </option>

          {courses.map(
            course => (
              <option
                key={course.id}
                value={course.id}
              >
                {getCourseName(
                  course
                )}
              </option>
            )
          )}
        </select>
      </div>


      <div style={styles.topBar}>
        <div style={styles.tabs}>
          {visibleTabs.map(
            tab => (
              <button
                key={tab.key}
                style={{
                  ...styles.tab,

                  ...(
                    activeTab ===
                      tab.key
                      ? styles
                          .tabActive
                      : {}
                  )
                }}
                onClick={() =>
                  selectRefreshSafeAssignmentTab(
                    tab.key
                  )
                }
              >
                {tab.label}
              </button>
            )
          )}
        </div>
      </div>


      {activeTab !==
        'all-grading' &&
          selectedCourse
            ?.self_eval_enabled ===
            true && (
          <div
            style={
              styles.pdfButtons
            }
          >
            <div
              style={
                styles.pdfGroup
              }
            >
              <button
                style={
                  styles.pdfBtn
                }
                disabled={
                  pdfLoading.mid
                }
                onClick={() =>
                  handleGeneratePdf(
                    'Mid-Term'
                  )
                }
              >
                {pdfLoading.mid
                  ? 'Preparing...'
                  : 'Print Mid-Term PDF'}
              </button>

              <div
                style={
                  styles
                    .pdfLinkList
                }
              >
                {pdfPackets.mid.map(
                  packet => (
                    <button
                      key={
                        packet
                          .instructorId ||
                        packet.label
                      }
                      type="button"
                      style={
                        styles
                          .pdfPacketBtn
                      }
                      onClick={() =>
                        handlePrintMeetingPacket(
                          'Mid-Term',
                          packet
                        )
                      }
                    >
                      {pdfPackets
                        .mid
                        .length === 1
                        ? 'Print Again'
                        : 'Print ' +
                          packet.label}
                    </button>
                  )
                )}
              </div>
            </div>


            <div
              style={
                styles.pdfGroup
              }
            >
              <button
                style={
                  styles.pdfBtn
                }
                disabled={
                  pdfLoading.final
                }
                onClick={() =>
                  handleGeneratePdf(
                    'Final'
                  )
                }
              >
                {pdfLoading.final
                  ? 'Preparing...'
                  : 'Print Final PDF'}
              </button>

              <div
                style={
                  styles
                    .pdfLinkList
                }
              >
                {pdfPackets.final.map(
                  packet => (
                    <button
                      key={
                        packet
                          .instructorId ||
                        packet.label
                      }
                      type="button"
                      style={
                        styles
                          .pdfPacketBtn
                      }
                      onClick={() =>
                        handlePrintMeetingPacket(
                          'Final',
                          packet
                        )
                      }
                    >
                      {pdfPackets
                        .final
                        .length === 1
                        ? 'Print Again'
                        : 'Print ' +
                          packet.label}
                    </button>
                  )
                )}
              </div>
            </div>
          </div>
        )}


      {activeTab !==
        'all-grading' &&
        !selectedCourse && (
        <div style={styles.muted}>
          Select a course to view assignments.
        </div>
      )}


      {activeTab !==
        'all-grading' &&
        selectedCourse
          ?.self_eval_enabled ===
          true &&
        (
          pdfNotice.mid ||
          pdfNotice.final
        ) && (
        <div
          style={
            styles.pdfNotice
          }
        >
          {pdfNotice.mid && (
            <div>
              {pdfNotice.mid}
            </div>
          )}

          {pdfNotice.final && (
            <div>
              {pdfNotice.final}
            </div>
          )}
        </div>
      )}


      {(
        activeTab ===
          'all-grading' ||
        selectedCourse
      ) && (
        <div style={styles.content}>
          {activeTab ===
            'all-grading' && (
            <AllGrading
              session={session}
              onOpenItem={
                handleOpenAllGradingItem
              }
            />
          )}

          {activeTab ===
            'grading' &&
            selectedCourse && (
            <AssignmentGrading
              key={
                selectedCourse.id
              }
              course={
                selectedCourse
              }
              session={session}
            />
          )}

          {activeTab ===
            'progress' &&
            selectedCourse &&
            canManageSelectedCourse &&
            selectedCourse
              ?.course_progress_enabled !==
              false && (
            <StudentProgress
              course={
                selectedCourse
              }
              session={session}
            />
          )}
        </div>
      )}
    </div>
  );
}

const styles = {
  heading: { margin: '0', fontSize: '24px', color: '#1f2933' },
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    marginBottom: '20px', gap: '16px', flexWrap: 'wrap'
  },
  courseSelect: {
    border: '1px solid #d7dee5', borderRadius: '8px', padding: '9px 11px',
    fontSize: '14px', background: 'white', minWidth: '280px'
  },
  muted: { color: '#66788a', fontSize: '14px', margin: '8px 0' },
  topBar: {
    position: 'sticky',
    top: 0,
    zIndex: 30,
    background: 'white',
    margin: '0 -4px 14px',
    padding: '0 4px',
    borderBottom: '1px solid #e3e9ee'
  },
  tabs: {
    display: 'flex',
    gap: '4px',
    width: '100%',
    marginBottom: 0,
    borderBottom: '2px solid #d7dee5',
    overflowX: 'auto',
    overflowY: 'hidden',
    WebkitOverflowScrolling: 'touch',
    overscrollBehaviorX: 'contain'
  },
  tab: {
    border: 'none', background: 'transparent', padding: '10px 16px',
    fontSize: '14px', fontWeight: '600', color: '#66788a', cursor: 'pointer',
    borderBottom: '2px solid transparent', marginBottom: '-2px',
    flexShrink: 0, whiteSpace: 'nowrap'
  },
  tabActive: { color: '#215b7d', borderBottom: '2px solid #215b7d' },
  content: {
    background: 'white', borderRadius: '12px', padding: '24px',
    border: '1px solid #d7dee5', boxShadow: '0 6px 20px rgba(0,0,0,0.08)'
  },
  pdfButtons: {
    display: 'flex',
    gap: '16px',
    alignItems: 'center',
    flexWrap: 'wrap',
    marginBottom: '16px'
  },
  pdfGroup: {
    display: 'flex', gap: '10px', alignItems: 'center', minWidth: '280px'
  },
  pdfLinkList: {
    display: 'flex',
    gap: '9px',
    alignItems: 'center',
    flexWrap: 'wrap'
  },
  pdfNotice: {
    marginBottom: '14px',
    padding: '9px 12px',
    border: '1px solid #ead49b',
    borderRadius: '8px',
    background: '#fff4d7',
    color: '#7b5b10',
    fontSize: '12px'
  },
  pdfBtn: {
    background: '#233746', color: 'white', border: 'none', borderRadius: '8px',
    padding: '9px 14px', fontSize: '13px', fontWeight: '600', cursor: 'pointer'
  },
  pdfPacketBtn: {
    border: '1px solid #b8c5ce',
    borderRadius: '8px',
    background: 'white',
    color: '#215b7d',
    padding: '7px 10px',
    fontSize: '12px',
    fontWeight: '700',
    cursor: 'pointer'
  },
  downloadLinkHidden: {
    color: '#215b7d', fontSize: '13px', fontWeight: '700',
    textDecoration: 'underline', visibility: 'hidden'
  },
};
